import { createFileRoute, redirect, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

/** Only same-origin relative paths are accepted as a post-login target. */
function safeNext(next: unknown): string | undefined {
  if (typeof next !== "string") return undefined;
  if (!next.startsWith("/") || next.startsWith("//")) return undefined;
  return next;
}

export const Route = createFileRoute("/login")({
  validateSearch: (s: Record<string, unknown>) => {
    const next = safeNext(s.next);
    return next ? { next } : {};
  },
  beforeLoad: async ({ search }) => {
    const { data } = await supabase.auth.getSession();
    if (data.session) {
      const next = safeNext((search as { next?: string }).next);
      throw next ? redirect({ href: next }) : redirect({ to: "/admin" });
    }
  },
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const search = Route.useSearch() as { next?: string };
  const next = safeNext(search.next);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    const redirectTo = `${window.location.origin}${next ?? "/admin"}`;
    const fn = mode === "signin"
      ? supabase.auth.signInWithPassword({ email, password })
      : supabase.auth.signUp({ email, password, options: { emailRedirectTo: redirectTo } });
    const { error } = await fn;
    setBusy(false);
    if (error) { toast.error(error.message); return; }
    if (mode === "signup") {
      toast.success("Account created. Signing you in…");
      await supabase.auth.signInWithPassword({ email, password });
    }
    if (next) { window.location.href = next; return; }
    navigate({ to: "/admin" });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <form onSubmit={submit} className="w-full max-w-sm space-y-5 luxury-card rounded-lg p-8">
        <div>
          <Link to="/" className="text-xs text-muted-foreground tracking-[0.2em] uppercase">← Back to site</Link>
          <h1 className="font-heading text-2xl font-semibold mt-2">
            {mode === "signin" ? "Admin Sign In" : "Create Admin Account"}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Atlanta Luxury Sprinters</p>
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.18em] block mb-2">Email</label>
          <input
            type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-input border border-border rounded-md px-4 py-3 text-sm"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.18em] block mb-2">Password</label>
          <input
            type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-input border border-border rounded-md px-4 py-3 text-sm"
          />
        </div>
        <button type="submit" disabled={busy} className="btn-primary rounded-md w-full">
          {busy ? "Please wait…" : mode === "signin" ? "Sign In" : "Create Account"}
        </button>
        <button
          type="button"
          onClick={() => setMode(mode === "signin" ? "signup" : "signin")}
          className="w-full text-xs text-muted-foreground hover:text-foreground"
        >
          {mode === "signin" ? "First time? Create your admin account" : "Already have an account? Sign in"}
        </button>
      </form>
    </div>
  );
}
